import { GripVertical, Plus, RotateCcw, Trash2 } from "lucide-react";
import { Fragment } from "react";
import { useLocalPref } from "@/client/hooks/use-local-pref";
import { currency, num, parseCurrencyInput } from "@/client/lib/format";
import { Button } from "../../ui/button";
import { Section } from "../view/section";
import type { PastedAreaRow } from "./areas-paste";
import { parseAreaNumber } from "./areas-paste";
import { GridCellInput as CI } from "./form-field";
import type { AreaFormRow } from "./types";
import { useAreasGridKeys } from "./use-areas-grid-keys";
import { useRowDrag } from "./use-row-drag";

const SHOW_INFLATION_COLS_KEY = "insurance.showInflationCols";
const INFLATION_YEARS = [1, 2, 3, 4] as const;
const INFLATION_FIELDS: Record<(typeof INFLATION_YEARS)[number], { pct: keyof AreaFormRow; amt: keyof AreaFormRow }> = {
  1: { pct: "inflationYear1Pct", amt: "calcInflationAmtYear1" },
  2: { pct: "inflationYear2Pct", amt: "calcInflationAmtYear2" },
  3: { pct: "inflationYear3Pct", amt: "calcInflationAmtYear3" },
  4: { pct: "inflationYear4Pct", amt: "calcInflationAmtYear4" },
};

interface Props {
  areas: AreaFormRow[];
  onAdd: () => void;
  onRemove: (index: number) => void;
  onUpdate: (index: number, field: keyof AreaFormRow, value: unknown) => void;
  onPasteRows: (startIndex: number, rows: PastedAreaRow[]) => void;
  gstEnabled: boolean;
}

const calc = "text-right text-xs tabular-nums bg-muted/10 whitespace-nowrap px-2";
const hCalc = "text-right bg-muted/30 text-[11px] whitespace-nowrap px-2";
const hInput = "text-right text-[11px] whitespace-nowrap px-1";

export function SectionAreasForm({
  areas,
  onAdd,
  onRemove,
  onUpdate,
  onPasteRows,
  gstEnabled,
}: Props): React.ReactElement {
  const showGst = gstEnabled || areas.some((r) => r.addGst);
  const [showInflationCols, setShowInflationCols] = useLocalPref(SHOW_INFLATION_COLS_KEY, false);
  const emptyColSpan = 21 + (showGst ? 1 : 0) + (showInflationCols ? 8 : 0);
  const { dragOverIndex, getHandleProps, getRowDropProps } = useRowDrag<AreaFormRow>({ rows: areas, onUpdate });
  const { tbodyRef, handleCellKeyDown, handleTablePaste } = useAreasGridKeys(areas.length, onAdd, onPasteRows);

  const numCell = (field: keyof AreaFormRow, value: number | null, i: number, w = "w-12"): React.ReactElement => (
    <td className="p-0.5">
      <CI
        value={value}
        onChange={(v) => onUpdate(i, field, v ? Number(v) : null)}
        w={w}
        onKeyDown={handleCellKeyDown}
      />
    </td>
  );

  const totals = { area: 0, reExcl: 0, reIncl: 0, inflTotal: 0, demoCost: 0, deprec: 0, drc: 0 };
  const inflYear = { 1: 0, 2: 0, 3: 0, 4: 0 } as Record<(typeof INFLATION_YEARS)[number], number>;
  for (const r of areas) {
    totals.area += r.area ?? 0;
    totals.reExcl += r.calcReinstatementExcl ?? 0;
    totals.reIncl += r.calcReinstatementIncl ?? 0;
    for (const y of INFLATION_YEARS) inflYear[y] += (r[INFLATION_FIELDS[y].amt] as number | null) ?? 0;
    totals.inflTotal += r.calcInflationTotal ?? 0;
    totals.demoCost += r.calcDemolitionCost ?? 0;
    totals.deprec += r.calcDepreciation ?? 0;
    totals.drc += r.calcDrcForFsl ?? 0;
  }

  return (
    <Section id="areas" title="Areas">
      <div className="flex items-start justify-between gap-4 mb-3">
        <p className="text-xs text-muted-foreground">
          Input fields on the left. Calculated outputs (shaded) scroll right. Months and inflation inherit from
          Financial Config unless overridden per row.
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setShowInflationCols(!showInflationCols)}
          data-testid="toggle-inflation-columns"
        >
          {showInflationCols ? "Hide inflation" : "Show inflation"}
        </Button>
      </div>
      <div className="overflow-x-auto border rounded-md">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="w-7 px-1" />
              <th className={hInput} style={{ minWidth: 140 }}>
                Accommodation
              </th>
              <th className={hInput}>Area (m²)</th>
              <th className={hInput}>E Yr</th>
              <th className={hInput}>Multiple</th>
              <th className={`${hCalc} border-l`}>Rate</th>
              {showGst && <th className={hInput}>GST</th>}
              <th className={hCalc}>Reinst Excl</th>
              <th className={hCalc}>Reinst Incl</th>
              <th className={hInput}>1st Mo</th>
              <th className={hInput}>Demo Mo</th>
              <th className={hInput}>Const Mo</th>
              <th className={hCalc}>Reinst Mo</th>
              {showInflationCols &&
                INFLATION_YEARS.map((y) => (
                  <Fragment key={y}>
                    <th className={hInput}>Inf Y{y}%</th>
                    <th className={hCalc}>Inf Y{y} $</th>
                  </Fragment>
                ))}
              <th className={hCalc}>Inf Total</th>
              <th className={hInput}>Demo %</th>
              <th className={hCalc}>Demo Cost</th>
              <th className={hCalc}>Demo Rate</th>
              <th className={hInput}>Dep %PA</th>
              <th className={hCalc}>Dep Tot%</th>
              <th className={hCalc}>Deprec</th>
              <th className={hCalc}>DRC FENZ Levy</th>
              <th className="w-7" />
            </tr>
          </thead>
          <tbody ref={tbodyRef} onPaste={handleTablePaste}>
            {areas.length === 0 ? (
              <tr>
                <td colSpan={emptyColSpan} className="text-center text-muted-foreground py-8">
                  No areas added. Click &quot;Add Row&quot; to start.
                </td>
              </tr>
            ) : (
              areas.map((row, i) => (
                <tr
                  key={row.tempId}
                  data-area-row="true"
                  className={`border-b hover:bg-accent/30 ${dragOverIndex === i ? "bg-accent/50" : ""}`}
                  {...getRowDropProps(i)}
                >
                  <td className="px-1">
                    <button
                      type="button"
                      aria-label="Reorder row"
                      data-testid={`area-row-drag-${row.tempId}`}
                      className="cursor-grab text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      {...getHandleProps(i)}
                    >
                      <GripVertical className="h-3.5 w-3.5" />
                    </button>
                  </td>
                  <td className="p-0.5">
                    <input
                      type="text"
                      data-cell="true"
                      value={row.description}
                      onChange={(e) => onUpdate(i, "description", e.target.value)}
                      onKeyDown={handleCellKeyDown}
                      className="w-full h-7 rounded border border-input bg-white px-1.5 text-xs"
                      placeholder="Accommodation..."
                    />
                  </td>
                  <td className="p-0.5">
                    <CI
                      type="text"
                      value={row.area}
                      onChange={(v) => onUpdate(i, "area", parseAreaNumber(v))}
                      onKeyDown={handleCellKeyDown}
                    />
                  </td>
                  <td className="p-0.5">
                    <CI
                      type="text"
                      value={row.eyrBuilt}
                      onChange={(v) => onUpdate(i, "eyrBuilt", parseAreaNumber(v))}
                      w="w-16"
                      onKeyDown={handleCellKeyDown}
                    />
                  </td>
                  <td className="p-0.5">
                    <CI value={row.multiple} onChange={(v) => onUpdate(i, "multiple", v ? Number(v) : null)} w="w-16" />
                  </td>
                  <td className={`${calc} border-l`}>
                    {row.rateOverride ? (
                      <span className="flex items-center gap-0.5 justify-end">
                        <CI
                          type="currency"
                          value={row.rate}
                          onChange={(v) => onUpdate(i, "rate", parseCurrencyInput(v))}
                          w="w-20"
                        />
                        <button
                          type="button"
                          onClick={() => onUpdate(i, "rateOverride", false)}
                          className="text-muted-foreground hover:text-primary"
                          title="Reset to auto"
                        >
                          <RotateCcw className="h-3 w-3" />
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onUpdate(i, "rateOverride", true)}
                        className="hover:underline"
                        title="Click to override"
                      >
                        {num(row.calcRate ?? 0)}
                      </button>
                    )}
                  </td>
                  {showGst && (
                    <td className="p-0.5 text-center">
                      <input
                        type="checkbox"
                        checked={row.addGst}
                        onChange={(e) => onUpdate(i, "addGst", e.target.checked)}
                        className="h-3.5 w-3.5"
                      />
                    </td>
                  )}
                  <td className={calc}>{currency(row.calcReinstatementExcl)}</td>
                  <td className={`${calc} font-medium`}>{currency(row.calcReinstatementIncl)}</td>
                  {numCell("firstYearMonths", row.firstYearMonths, i)}
                  {numCell("demoPlanMonths", row.demoPlanMonths, i)}
                  {numCell("constructionMonths", row.constructionMonths, i)}
                  <td className={calc}>{num(row.calcReinstatementMonths ?? 0)}</td>
                  {showInflationCols &&
                    INFLATION_YEARS.map((y) => {
                      const { pct, amt } = INFLATION_FIELDS[y];
                      return (
                        <Fragment key={y}>
                          {numCell(pct, row[pct] as number | null, i)}
                          <td className={calc}>{currency(row[amt] as number | null)}</td>
                        </Fragment>
                      );
                    })}
                  <td className={`${calc} font-medium`}>{currency(row.calcInflationTotal)}</td>
                  {numCell("demolitionPercentage", row.demolitionPercentage, i)}
                  <td className={calc}>{currency(row.calcDemolitionCost)}</td>
                  <td className={calc}>{currency(row.calcDemolitionRate)}</td>
                  {numCell("percentagePa", row.percentagePa, i)}
                  <td className={calc}>
                    {row.calcDepreciationTotalPct != null ? `${row.calcDepreciationTotalPct.toFixed(1)}%` : "—"}
                  </td>
                  <td className={calc}>{currency(row.calcDepreciation)}</td>
                  <td className={`${calc} font-medium`}>{currency(row.calcDrcForFsl)}</td>
                  <td className="px-1">
                    <button
                      type="button"
                      onClick={() => onRemove(i)}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))
            )}
            {areas.length > 0 && (
              <tr className="border-t-2 bg-muted/20 font-medium">
                <td />
                <td className="px-2 py-1.5 text-xs">Totals</td>
                <td className="text-right text-xs tabular-nums px-2">{num(totals.area)}</td>
                <td />
                <td colSpan={showGst ? 3 : 2} />
                <td className={calc}>{currency(totals.reExcl)}</td>
                <td className={calc}>{currency(totals.reIncl)}</td>
                <td colSpan={3} />
                <td />
                {showInflationCols &&
                  INFLATION_YEARS.map((y) => (
                    <Fragment key={y}>
                      <td />
                      <td className={calc}>{currency(inflYear[y])}</td>
                    </Fragment>
                  ))}
                <td className={calc}>{currency(totals.inflTotal)}</td>
                <td />
                <td className={calc}>{currency(totals.demoCost)}</td>
                <td />
                <td />
                <td />
                <td className={calc}>{currency(totals.deprec)}</td>
                <td className={calc}>{currency(totals.drc)}</td>
                <td />
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="mt-3">
        <Button type="button" variant="outline" size="sm" onClick={onAdd} data-testid="add-area-row">
          <Plus className="h-3.5 w-3.5" />
          Add Row
        </Button>
      </div>
    </Section>
  );
}
